import { Injectable } from '@angular/core';
import { supabase } from './supabase.client';
import {
  PostgrestResponse,
  PostgrestSingleResponse,
} from '@supabase/supabase-js';
import {
  Project,
  ProjectExt,
  ProjectStatesCountByYear,
} from '../shared/models/projects.models';
import { TABLE_NAMES, VIEW_NAMES } from './supabase.constants';
import { toApi, toSnakeCase } from '../shared/utils/case-convert';
import { PROJECT_STATE_VALUES } from '../shared/constants/projects.constants';
import { PAYMENT_STATUS } from '../shared/models/payment.models';

@Injectable({
  providedIn: 'root',
})
export class SupabaseDbProjectServiceService {
  public async getList(
    filter?: Partial<ProjectExt>,
    sort?: { field: string; ascending: boolean },
    year?: number,
  ): Promise<PostgrestResponse<ProjectExt>> {
    const categoryJoin =
      year != null ? 'categories!inner(*)' : 'categories (*)';
    let q = supabase.from(VIEW_NAMES.PROJECTS_WITH_LAST_PAYMENT).select(`
      *,
      category: ${categoryJoin},
      owner: contestants (*)
      `);

    if (filter) {
      q = q.match(toApi(filter));
    }
    if (year != null) {
      q = q.eq('category.year', year);
    }
    if (sort) {
      q = q.order(sort.field, { ascending: sort.ascending });
    }
    return q;
  }

  public async getProjectsExt(
    page: number,
    pageSize: number,
    sortField: string,
    sortOrder: 'asc' | 'desc' | '',
    title?: string,
    companyName?: string,
    ownerEmail?: string,
    categoryYears?: number[],
    categoryIds?: string[],
    states?: PROJECT_STATE_VALUES[],
    paymentStatuses?: PAYMENT_STATUS[],
  ) {
    let q = supabase.from(VIEW_NAMES.PROJECTS_WITH_LAST_PAYMENT).select(
      `
      *,
      category: categories!inner(*),
      owner: v_contestants_ext_fast!inner(*),
      feedbacks: project_feedbacks(*)
      `,
      { count: 'exact' },
    );

    if (title?.trim()) {
      q = q.ilike('title', `%${title.trim()}%`);
    }
    if (companyName?.trim()) {
      q = q.ilike('owner.company_name', `%${companyName.trim()}%`);
    }
    if (ownerEmail?.trim()) {
      q = q.ilike('owner.email', `%${ownerEmail.trim()}%`);
    }
    if (categoryYears && categoryYears.length > 0) {
      q = q.in('category.year', categoryYears);
    }
    if (categoryIds && categoryIds.length > 0) {
      q = q.in('category_id', categoryIds);
    }
    if (states && states.length > 0) {
      q = q.in('state', states);
    }
    if (paymentStatuses && paymentStatuses.length > 0) {
      q = q.in('last_payment_status', paymentStatuses);
    }

    const sortBy: string = toSnakeCase(sortField);
    const isAsc: boolean = sortOrder === 'asc';

    switch (sortBy) {
      case 'company_name':
        q = q.order('owner(company_name)', { ascending: isAsc });
        break;
      case 'owner_email':
        q = q.order('owner(email)', { ascending: isAsc });
        break;
      case 'category_year':
        q = q.order('category(year)', { ascending: isAsc });
        break;
      case 'category_names':
        q = q.order('category(name)', { ascending: isAsc });
        break;
      case 'states':
        q = q.order('state', { ascending: isAsc });
        break;
      default:
        if (sortBy) {
          q = q.order(sortBy, { ascending: isAsc });
        }
        break;
    }

    if (sortBy !== 'created_at') {
      q = q.order('created_at', { ascending: false });
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    q = q.range(from, to);

    const { data, count, error } = await q;

    if (error) throw error;

    return { data: data ?? [], count: count ?? 0 };
  }

  public async getById(id: string): Promise<PostgrestSingleResponse<Project>> {
    return supabase
      .from(TABLE_NAMES.PROJECTS)
      .select('*')
      .eq('id', id)
      .single();
  }

  public async getExtById(
    id: string,
  ): Promise<PostgrestSingleResponse<ProjectExt>> {
    return supabase
      .from(TABLE_NAMES.PROJECTS)
      .select(
        `
      *,
      category: categories (*),
      owner: contestants (*)
      `,
      )
      .eq('id', id)
      .single();
  }

  public async getWithOwnerExtById(
    id: string,
  ): Promise<PostgrestSingleResponse<ProjectExt>> {
    return supabase
      .from(TABLE_NAMES.PROJECTS)
      .select(
        `
      *,
      category: categories (*),
      owner: v_contestants_ext (*)
      `,
      )
      .eq('id', id)
      .single();
  }

  public async update(
    id: string,
    patch: Partial<Project>,
  ): Promise<PostgrestSingleResponse<Project>> {
    return supabase
      .from(TABLE_NAMES.PROJECTS)
      .update(toApi(patch, false))
      .eq('id', id)
      .select()
      .single();
  }

  public async delete(id: string): Promise<PostgrestSingleResponse<null>> {
    return supabase
      .from(TABLE_NAMES.PROJECTS)
      .delete()
      .eq('id', id)
      .select()
      .single();
  }

  public async create(project: any): Promise<Project> {
    const { data, error } = await supabase
      .from(TABLE_NAMES.PROJECTS)
      .insert(toApi(project, false))
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  public async getNonDraftProjectsCount(id: string, year: number): Promise<{ id: string }[]> {
    const { data, error } = await supabase
      .from(TABLE_NAMES.PROJECTS)
      .select('id, category:categories!inner(year)')
      .eq('owner_id', id)
      .neq('state', PROJECT_STATE_VALUES.DRAFT)
      .eq('category.year', year);
    if (error) throw error;
    return data;
  }

  public async hasPaidProject(id: string, year: number): Promise<boolean> {
    const {data, error} = await supabase
      .from(VIEW_NAMES.PROJECTS_WITH_LAST_PAYMENT)
      .select('id, category:categories!inner(year)')
      .eq('owner_id', id)
      .eq('last_payment_status', PAYMENT_STATUS.SUCCEEDED)
      .eq('category.year', year)
      .limit(1);
    if (error) throw error;
    return Boolean(data?.length);
  }

  public async getProjectStatesCountByYear(
    year: number,
  ): Promise<PostgrestSingleResponse<ProjectStatesCountByYear>> {
    const { data, error } = await supabase
      .from(VIEW_NAMES.PROJECT_STATES_COUNT_BY_YEAR)
      .select('*')
      .eq('year', year)
      .single();
    if (error) throw error;
    return data;
  }

  public async getProjectPayment(id: string): Promise<any> {
    const { data, error } = await supabase
      .from(VIEW_NAMES.LATEST_PROJECT_PAYMENTS)
      .select('status')
      .eq('project_id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  }
}
